import * as Sentry from '@sentry/browser';
import { LitElement, html } from 'lit';
import '../icons/checkmark-icon.js';
import '../icons/info-icon.js';
import '../icons/success-icon.js';

/**
 * Quote Terms Accordion Component
 * Displays terms & conditions as collapsible accordions with individual checkboxes
 * Implements progressive disclosure pattern for better mobile UX
 */
class QuoteTermsAccordion extends LitElement {
    static properties = {
        terms: { type: Array },
        acceptedTerms: { type: Set, state: true },
        openTermSeq: { type: Number, state: true },
    };

    // Use Light DOM for Tailwind compatibility
    createRenderRoot() {
        return this;
    }

    constructor() {
        super();
        this.terms = /** @type {Array<{seq: number, term: string}>} */ ([]);
        this.acceptedTerms = new Set();
        this.openTermSeq = null;
    }

    firstUpdated() {
        // Open the first term by default when component loads
        if (this.sortedTerms.length > 0) {
            this.openTermSeq = this.sortedTerms[0].seq;
        }
    }

    get sortedTerms() {
        // Sort terms by seq property
        return [...this.terms].sort((a, b) => a.seq - b.seq);
    }

    get allTermsAccepted() {
        return this.terms.length > 0 && this.acceptedTerms.size === this.terms.length;
    }

    handleAccordionToggle(seq, event) {
        // Toggle the accordion open/closed state
        if (event.target.checked) {
            this.openTermSeq = seq;
        } else {
            this.openTermSeq = null;
        }
    }

    handleTermCheckbox(seq, event) {
        const newAcceptedTerms = new Set(this.acceptedTerms);

        if (event.target.checked) {
            newAcceptedTerms.add(seq);

            // Find the next unaccepted term
            const currentIndex = this.sortedTerms.findIndex((t) => t.seq === seq);
            const nextUnacceptedTerm = this.sortedTerms
                .slice(currentIndex + 1)
                .find((t) => !newAcceptedTerms.has(t.seq));

            // If this is not the last term, collapse current and open next unaccepted
            if (nextUnacceptedTerm) {
                // Use setTimeout to ensure state updates before changing accordion
                setTimeout(() => {
                    this.openTermSeq = nextUnacceptedTerm.seq;
                }, 300); // Small delay for smooth transition
            } else {
                // If this is the last term or all others are accepted, close it
                setTimeout(() => {
                    this.openTermSeq = null;
                }, 300);
            }
        } else {
            newAcceptedTerms.delete(seq);
        }

        this.acceptedTerms = newAcceptedTerms;

        const accepted = event.target.checked;
        const term = this.sortedTerms.find((t) => t.seq === seq);
        Sentry.addBreadcrumb({
            category: 'terms',
            message: `Term ${accepted ? 'accepted' : 'unaccepted'}: seq ${seq}`,
            level: 'info',
            data: { title: term?.title || null, seq },
        });

        // Emit custom event for parent components
        this.dispatchEvent(
            new CustomEvent('terms-acceptance-changed', {
                detail: {
                    allAccepted: this.allTermsAccepted,
                    acceptedCount: this.acceptedTerms.size,
                    totalCount: this.terms.length,
                },
                bubbles: true,
                composed: true,
            })
        );
    }

    getTermTitle(term) {
        // Special terms provide an explicit title
        if (term.title) {
            return term.title;
        }
        // API terms: extract title from term text (before first colon if present)
        const text = term.term || '';
        const colonIndex = text.indexOf(':');
        if (colonIndex > 0 && colonIndex < 50) {
            return text.substring(0, colonIndex);
        }
        // Otherwise use first 50 chars
        return text.substring(0, 50) + (text.length > 50 ? '...' : '');
    }

    renderTerm(term, displayNumber) {
        const isChecked = this.acceptedTerms.has(term.seq);
        const isOpen = this.openTermSeq === term.seq;
        const termId = `term-${term.seq}`;
        const title = this.getTermTitle(term);

        return html`
            <div
                class="collapse collapse-plus bg-base-200 border-2 ${isChecked
                    ? 'border-peppermint'
                    : 'border-base-200'} mb-3"
            >
                <input
                    type="checkbox"
                    id="${termId}-collapse"
                    .checked="${isOpen}"
                    @change="${(e) => this.handleAccordionToggle(term.seq, e)}"
                    aria-label="Expand term ${displayNumber}"
                />
                <div class="collapse-title text-lg font-medium pr-12">
                    <div class="flex items-center gap-3">
                        <span class="badge badge-primary badge-sm">${displayNumber}</span>
                        <span class="flex-1">${title}</span>
                        ${isChecked
                            ? html`<checkmark-icon
                                  class="text-peppermint shrink-0"
                              ></checkmark-icon>`
                            : ''}
                    </div>
                </div>
                <div class="collapse-content">
                    <div class="pt-2">
                        <!-- Term content: rich HTML component or plain text -->
                        ${term.templateContent
                            ? html`<div class="mb-4">${term.templateContent}</div>`
                            : html`<div
                                  class="prose prose-sm max-w-none mb-4 text-gray-700 leading-relaxed"
                              >
                                  <p>${term.term}</p>
                              </div>`}

                        <!-- Acceptance checkbox -->
                        <div class="form-control">
                            <label
                                class="label cursor-pointer justify-start gap-3 bg-white p-4 rounded-lg border-2 ${isChecked
                                    ? 'border-peppermint bg-peppermint/5'
                                    : 'border-gray-300'}"
                            >
                                <input
                                    type="checkbox"
                                    class="checkbox checkbox-accent"
                                    .checked="${isChecked}"
                                    @change="${(e) => this.handleTermCheckbox(term.seq, e)}"
                                    aria-label="Accept term ${displayNumber}"
                                />
                                <span class="label-text font-medium text-base">
                                    ${term.seq === 0
                                        ? 'I have read and accept these General Terms'
                                        : 'I have read and accept these Special Terms'}
                                </span>
                                ${isChecked
                                    ? html`<checkmark-icon
                                          class="text-peppermint ml-auto"
                                      ></checkmark-icon>`
                                    : ''}
                            </label>
                        </div>
                    </div>
                </div>
            </div>
        `;
    }

    render() {
        if (!this.terms || this.terms.length === 0) {
            return html`
                <div class="alert alert-row alert-info">
                    <info-icon class="shrink-0"></info-icon>
                    <span class="text-left">No additional terms apply to this quote.</span>
                </div>
            `;
        }

        return html`
            <div class="space-y-4">
                <!-- Header -->
                <div class="mb-4">
                    <h3 class="text-xl font-bold mb-2">Terms & Conditions</h3>
                    <p class="text-sm text-gray-600">
                        Please read and accept each term below to proceed with your quote
                        acceptance.
                    </p>
                    <div class="mt-2">
                        <span class="text-sm font-medium">
                            Accepted:
                            <span
                                class="${this.allTermsAccepted
                                    ? 'text-peppermint'
                                    : 'text-gray-600'}"
                            >
                                ${this.acceptedTerms.size} / ${this.terms.length}
                            </span>
                        </span>
                    </div>
                </div>

                <!-- Terms accordions -->
                <div class="space-y-2">
                    ${this.sortedTerms.map((term, index) => this.renderTerm(term, index + 1))}
                </div>

                <!-- All terms accepted indicator -->
                ${this.allTermsAccepted
                    ? html`
                          <div class="alert alert-row alert-success">
                              <success-icon class="shrink-0"></success-icon>
                              <span class="text-left"
                                  >All terms have been accepted. You may now proceed with quote
                                  acceptance.</span
                              >
                          </div>
                      `
                    : ''}
            </div>
        `;
    }
}

customElements.define('quote-terms-accordion', QuoteTermsAccordion);
